// Nieblokujący zdalny logger błędów -> Firebase Firestore.
//
// Cel: gdy jakakolwiek procedura (ajax lub fetch) wywali się u klienta,
// chcemy widzieć NA ŻYWO: jaka procedura, do jakiego API, z jakim requestem
// i z jakim statusem/odpowiedzią. Logi lecą do Firestore w tle (fire-and-forget)
// i NIGDY nie mogą wywrócić aplikacji ani zablokować UI.
//
// Konfiguracja Firebase jest zaszyta w ./firebaseConfig (klucze web nie są
// tajne). Jeśli config jest pusty, logger po prostu wypisuje do konsoli i nie
// wysyła nic zdalnie (bez błędów).

import jwt_decode from "jwt-decode";
import packageJson from "../../package.json";
import { firebaseConfig, LOG_COLLECTION } from "./firebaseConfig";

const MAX_TEXT = 4000; // limit długości pojedynczego pola tekstowego w logu
const APP_VERSION = packageJson.version; // wersja brana wprost z package.json

let db = null; // instancja Firestore (lazy)
let setDocFn = null;
let docFn = null;
let serverTimestampFn = null;
let initPromise = null;
let enabled = false;
let ajaxHookInstalled = false;

const COLLECTION = LOG_COLLECTION;

// Lazy, jednorazowa inicjalizacja Firebase. Importy dynamiczne, żeby nie
// obciążać głównego bundla, gdy logger nie jest skonfigurowany.
async function ensureInit() {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
      // Brak konfiguracji -> tryb tylko-konsola.
      enabled = false;
      return;
    }

    try {
      const { initializeApp, getApps } = await import("firebase/app");
      const {
        getFirestore,
        initializeFirestore,
        persistentLocalCache,
        persistentMultipleTabManager,
        doc,
        setDoc,
        serverTimestamp,
      } = await import("firebase/firestore");

      const app = getApps().length
        ? getApps()[0]
        : initializeApp(firebaseConfig);

      // Trwały cache (IndexedDB): zakolejkowane offline zapisy przeżywają
      // zamknięcie/przeładowanie karty i dosyłają się przy następnym starcie
      // online. Gdyby IndexedDB był niedostępny (np. tryb prywatny) - fallback
      // na zwykły cache w pamięci, żeby logowanie dalej działało.
      try {
        db = initializeFirestore(app, {
          localCache: persistentLocalCache({
            tabManager: persistentMultipleTabManager(),
          }),
        });
      } catch (persistErr) {
        // eslint-disable-next-line no-console
        console.warn(
          "[remoteLogger] trwały cache niedostępny, fallback do pamięci:",
          persistErr
        );
        db = getFirestore(app);
      }

      docFn = doc;
      setDocFn = setDoc;
      serverTimestampFn = serverTimestamp;
      enabled = true;
    } catch (e) {
      // Nie udało się załadować/zainicjować Firebase - nie blokujemy aplikacji.
      enabled = false;
      // eslint-disable-next-line no-console
      console.warn("[remoteLogger] Firebase init failed:", e);
    }
  })();

  return initPromise;
}

function getToken() {
  try {
    const name = "token=";
    const cookies = decodeURIComponent(document.cookie).split(";");
    for (let c of cookies) {
      c = c.trim();
      if (c.startsWith(name)) return c.substring(name.length);
    }
  } catch (_) {}
  return undefined;
}

function getUserContext() {
  try {
    const token = getToken();
    if (!token) return { anonymous: true };
    const decoded = jwt_decode(token);
    return {
      id: decoded?.id ?? decoded?.userId ?? null,
      login: decoded?.login ?? decoded?.username ?? null,
      role: decoded?.role ?? null,
    };
  } catch (_) {
    return { anonymous: true };
  }
}

function truncate(value) {
  if (value == null) return value;
  const str = typeof value === "string" ? value : String(value);
  return str.length > MAX_TEXT ? str.slice(0, MAX_TEXT) + "…[obcięto]" : str;
}

// Bezpieczne streszczenie requestu - bez wielkich blobów/plików i danych binarnych.
export function summarizeRequest(data) {
  try {
    if (data == null) return null;

    if (typeof data === "string") {
      return truncate(data);
    }

    if (typeof FormData !== "undefined" && data instanceof FormData) {
      const fields = {};
      for (const [key, val] of data.entries()) {
        if (typeof File !== "undefined" && val instanceof File) {
          fields[key] = `[File name=${val.name} size=${val.size} type=${val.type}]`;
        } else if (typeof Blob !== "undefined" && val instanceof Blob) {
          fields[key] = `[Blob size=${val.size} type=${val.type}]`;
        } else {
          fields[key] = truncate(val);
        }
      }
      return fields;
    }

    if (typeof data === "object") {
      const out = {};
      for (const key of Object.keys(data)) {
        const val = data[key];
        if (typeof File !== "undefined" && val instanceof File) {
          out[key] = `[File name=${val.name} size=${val.size}]`;
        } else if (typeof Blob !== "undefined" && val instanceof Blob) {
          out[key] = `[Blob size=${val.size}]`;
        } else if (val && typeof val === "object") {
          out[key] = truncate(JSON.stringify(val));
        } else {
          out[key] = truncate(val);
        }
      }
      return out;
    }

    return truncate(data);
  } catch (_) {
    return "[nie udało się streścić requestu]";
  }
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

// Krótki, losowy kawałek id (żeby dokumenty się nie nadpisywały w tej samej
// sekundzie dla tego samego usera).
function randomChunk() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID().split("-")[0]; // 8 znaków hex
    }
  } catch (_) {}
  return Math.random().toString(36).slice(2, 10);
}

// ID dokumentu w formacie: YYYY-MM-DD_HH-mm-ss_login_kawałekId
function buildLogId(user) {
  const d = new Date();
  const date =
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `_${pad2(d.getHours())}-${pad2(d.getMinutes())}-${pad2(d.getSeconds())}`;

  const rawLogin = (user && (user.login || user.id)) || "anon";
  // Doc ID nie może zawierać "/" ani znaków specjalnych - sanityzujemy.
  const login =
    String(rawLogin).replace(/[^\w.-]/g, "").slice(0, 40) || "anon";

  return `${date}_${login}_${randomChunk()}`;
}

// Główna funkcja logująca. Fire-and-forget: nigdy nie rzuca i nie zwraca
// niczego, na co trzeba czekać.
export function logRemoteError(details = {}) {
  const payload = {
    ts: new Date().toISOString(),
    procedure: details.procedure || "unknown",
    method: details.method || null,
    url: details.url || null,
    status: details.status ?? null,
    message: truncate(details.message) || null,
    response: truncate(details.response) || null,
    request: details.request !== undefined ? details.request : null,
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    appVersion: APP_VERSION || null,
    location: typeof window !== "undefined" ? window.location.href : null,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    user: getUserContext(),
    extra: details.extra || null,
  };

  // Zawsze zostaw ślad w konsoli (przydatne lokalnie i przy braku Firebase).
  // eslint-disable-next-line no-console
  console.error("[remoteLogger]", payload.procedure, payload);

  // Wysyłka w tle - błędy loggera nie mogą wpływać na aplikację.
  ensureInit()
    .then(() => {
      if (!enabled || !db) return;
      const id = buildLogId(payload.user);
      return setDocFn(docFn(db, COLLECTION, id), {
        ...payload,
        createdAt: serverTimestampFn(),
      });
    })
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.warn("[remoteLogger] send failed:", e);
    });
}

// Globalny hook na WSZYSTKIE błędy $.ajax (jQuery jest globalne w projekcie).
// Dzięki temu każda procedura z api_*.js / authprovider.js jest logowana
// automatycznie, bez dotykania każdej z osobna.
export function installGlobalAjaxErrorLogger($) {
  if (ajaxHookInstalled || !$ || !$.fn) return;
  ajaxHookInstalled = true;

  $(document).ajaxError(function (event, jqXHR, ajaxSettings, thrownError) {
    try {
      // Przerwane żądania to nie realny błąd.
      if (jqXHR && jqXHR.statusText === "abort") return;

      const status = jqXHR ? jqXHR.status : null;

      logRemoteError({
        procedure: "ajax",
        method: ajaxSettings ? ajaxSettings.type : null,
        url: ajaxSettings ? ajaxSettings.url : null,
        status,
        message:
          thrownError ||
          (jqXHR && jqXHR.statusText) ||
          "ajax error",
        response: jqXHR ? jqXHR.responseText : null,
        request: summarizeRequest(ajaxSettings ? ajaxSettings.data : null),
        extra: {
          // status 0 = brak połączenia / serwer nieosiągalny (offline)
          offline: status === 0,
        },
      });
    } catch (_) {
      // pod żadnym pozorem nie wywracamy handlera
    }
  });
}

// Inicjalizacja loggera - wołane raz przy starcie aplikacji.
export function initRemoteLogger($) {
  // Rozgrzej inicjalizację Firebase (nie blokuje).
  ensureInit();

  if ($) installGlobalAjaxErrorLogger($);

  // W trybie dev wystaw logger na window, żeby dało się go przetestować
  // z konsoli: window.logRemoteError({ procedure: "test", ... })
  if (
    typeof window !== "undefined" &&
    process.env.NODE_ENV !== "production"
  ) {
    window.logRemoteError = logRemoteError;
  }

  // Złap też niewyłapane wyjątki i odrzucone promisy (best-effort).
  if (typeof window !== "undefined") {
    window.addEventListener("error", (e) => {
      logRemoteError({
        procedure: "window.onerror",
        message: e?.message,
        extra: {
          filename: e?.filename,
          lineno: e?.lineno,
          colno: e?.colno,
        },
      });
    });

    window.addEventListener("unhandledrejection", (e) => {
      logRemoteError({
        procedure: "unhandledrejection",
        message:
          (e?.reason && (e.reason.message || String(e.reason))) ||
          "unhandled promise rejection",
      });
    });
  }
}
